import React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import { animate } from "motion";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

import { getPublicSiteSx } from "./publicSiteStyles";

const MAP_BG_DEFAULT =
  "url(https://ouro-public-assets.s3.eu-west-2.amazonaws.com/home/csmap-scene-2.jpg)";
const MAP_TARGET_OPACITY = 0.16;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  );
}

function useEntranceAnimation(ref, { opacity = 0, y = 24, delay = 0, duration = 0.7 } = {}) {
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (prefersReducedMotion()) {
      el.style.opacity = "1";
      el.style.transform = "none";
      return;
    }

    el.style.opacity = String(opacity);
    el.style.transform = `translateY(${y}px)`;
    const controls = animate(
      el,
      { opacity: 1, transform: "translateY(0px)" },
      { duration, easing: [0.25, 0.1, 0.25, 1], delay }
    );
    return () => { try { controls?.stop?.(); } catch {} };
  }, []);
}

function useMapBgAnimation(ref) {
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      el.style.opacity = String(MAP_TARGET_OPACITY);
      return;
    }
    const controls = animate(
      el,
      { opacity: [0, MAP_TARGET_OPACITY], transform: ["scale(1.08)", "scale(1)"] },
      { duration: 1.6, easing: [0.25, 0.1, 0.25, 1] }
    );
    return () => { try { controls?.stop?.(); } catch {} };
  }, []);
}

export default function PublicPageLayout({
  eyebrow,
  title,
  description,
  mapBackground,
  children,
}) {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);

  const mapBgRef = React.useRef(null);
  const eyebrowRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const descRef = React.useRef(null);
  const contentRef = React.useRef(null);

  useMapBgAnimation(mapBgRef);
  useEntranceAnimation(eyebrowRef, { y: 14, delay: 0.15, duration: 0.55 });
  useEntranceAnimation(titleRef, { y: 20, delay: 0.25, duration: 0.7 });
  useEntranceAnimation(descRef, { y: 16, delay: 0.4, duration: 0.6 });
  useEntranceAnimation(contentRef, { y: 30, delay: 0.55, duration: 0.75 });

  const heroSx = mapBackground
    ? { ...sx.heroSection, "&:after": { display: "none" } }
    : sx.heroSection;

  const mapBgImage =
    typeof mapBackground === "string" ? `url(${mapBackground})` : MAP_BG_DEFAULT;

  return (
    <Box sx={sx.pageRoot}>
      <Container maxWidth="xl">
        <Box sx={heroSx}>
          {/* Real DOM element for map background — animated directly, no pseudo-element */}
          {mapBackground && (
            <Box
              ref={mapBgRef}
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: mapBgImage,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(1px)",
                pointerEvents: "none",
                zIndex: -1,
                opacity: 0,
                transformOrigin: "center center",
              }}
            />
          )}
          <Box sx={sx.heroPanel}>
            {eyebrow ? (
              <Typography ref={eyebrowRef} sx={{ ...sx.eyebrow, opacity: 0 }}>
                {eyebrow}
              </Typography>
            ) : null}
            <Typography
              ref={titleRef}
              component="h1"
              sx={{ ...sx.heroTitle, opacity: 0 }}
            >
              {title}
            </Typography>
            {description ? (
              <Typography ref={descRef} sx={{ ...sx.heroSubtitle, opacity: 0 }}>
                {description}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box ref={contentRef} sx={{ opacity: 0 }}>
          {children}
        </Box>
      </Container>
    </Box>
  );
}

PublicPageLayout.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  mapBackground: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  children: PropTypes.node,
};
